<template>
  <div class="page-card competition-page">
    <div class="page-head">
      <h2 class="page-title">赛事中心</h2>
      <el-button type="primary" icon="el-icon-refresh" @click="refreshAll">刷新</el-button>
    </div>

    <el-tabs v-model="activeTab" @tab-click="handleTabChange">
      <el-tab-pane label="赛事管理" name="events">
        <div class="toolbar">
          <div class="toolbar-left">
            <el-input v-model="eventQuery.keyword" clearable placeholder="赛事名称/地点" style="width: 220px" />
            <el-select v-model="eventQuery.status" clearable placeholder="状态" style="width: 130px">
              <el-option v-for="item in eventStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-button type="primary" @click="fetchEvents">查询</el-button>
          </div>
          <el-button v-if="canCreate" type="primary" icon="el-icon-plus" @click="openEventDialog()">新增赛事</el-button>
        </div>

        <el-table :data="events" border v-loading="eventLoading">
          <el-table-column label="赛事" min-width="260">
            <template slot-scope="{ row }">
              <div class="event-cell">
                <img v-if="row.coverUrl" class="event-cover" :src="fullUrl(row.coverUrl)" alt="">
                <div v-else class="event-cover event-cover-empty">封面</div>
                <div class="event-info">
                  <div class="event-title">{{ row.title || '-' }}</div>
                  <div class="event-subtitle muted-text">{{ row.subtitle || '-' }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="地点" min-width="200">
            <template slot-scope="{ row }">
              <div>{{ row.fullAddress || locationLabel(row) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="赛事时间" min-width="180">
            <template slot-scope="{ row }">
              <div>{{ row.eventStartAt || '-' }}</div>
              <div class="muted-text">{{ row.eventEndAt || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="报名时间" min-width="180">
            <template slot-scope="{ row }">
              <div>{{ row.registrationStartAt || '-' }}</div>
              <div class="muted-text">{{ row.registrationEndAt || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="报名费" width="110">
            <template slot-scope="{ row }">{{ feeLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="报名/录用" width="120">
            <template slot-scope="{ row }">
              <div>{{ row.registrationCount || 0 }} / {{ row.acceptedCount || 0 }}</div>
              <div v-if="row.pendingCount" class="muted-text">待审 {{ row.pendingCount }}</div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">
              <el-tag :type="eventStatusTagType(row.status)" size="small">{{ eventStatusLabel(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="updatedAt" label="更新时间" width="170" />
          <el-table-column label="操作" width="220" fixed="right">
            <template slot-scope="{ row }">
              <el-button v-if="canUpdate" type="text" @click="openEventDialog(row)">编辑</el-button>
              <el-button type="text" @click="viewRegistrations(row)">报名</el-button>
              <el-button v-if="row.status !== 2 && canPublish" type="text" @click="publishEvent(row)">发布</el-button>
              <el-button v-if="row.status === 2 && canOffline" type="text" class="danger-link" @click="offlineEvent(row)">下架</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="报名管理" name="registrations">
        <div class="toolbar">
          <div class="toolbar-left">
            <el-select
              v-model="registrationQuery.competitionId"
              clearable
              filterable
              placeholder="选择赛事"
              style="width: 260px"
            >
              <el-option
                v-for="item in eventOptions"
                :key="item.id"
                :label="item.title"
                :value="item.id"
              />
            </el-select>
            <el-select v-model="registrationQuery.status" clearable placeholder="报名状态" style="width: 140px">
              <el-option v-for="item in registrationStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-input v-model="registrationQuery.keyword" clearable placeholder="艺人/手机号" style="width: 200px" />
            <el-button type="primary" @click="fetchRegistrations">查询</el-button>
          </div>
        </div>

        <el-table :data="registrations" border v-loading="registrationLoading">
          <el-table-column label="赛事" min-width="180" prop="competitionTitle" />
          <el-table-column label="艺人" min-width="220">
            <template slot-scope="{ row }">
              <div class="artist-cell">
                <img v-if="row.artistAvatarUrl" class="artist-avatar" :src="fullUrl(row.artistAvatarUrl)" alt="">
                <div v-else class="artist-avatar artist-avatar-empty">头像</div>
                <div class="artist-info">
                  <div>{{ row.applicantName || row.artistName || '-' }}</div>
                  <div class="muted-text">{{ row.applicantMobile || row.artistMobile || '-' }}</div>
                  <el-button
                    v-if="row.artistUserId && canViewArtistDetail(row)"
                    type="text"
                    class="artist-detail-link"
                    @click="openArtistDetail(row)"
                  >
                    详情
                  </el-button>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="applicantRemark" label="报名备注" min-width="160" show-overflow-tooltip />
          <el-table-column label="缴费" width="120">
            <template slot-scope="{ row }">
              <div>{{ payStatusLabel(row.payStatus) }}</div>
              <div v-if="row.payAmount > 0" class="muted-text">¥{{ row.payAmount }}</div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110">
            <template slot-scope="{ row }">
              <el-tag :type="registrationStatusTagType(row.status)" size="small">
                {{ registrationStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="报名时间" width="170" />
          <el-table-column prop="auditedAt" label="审核时间" width="170" />
          <el-table-column label="操作" width="180" fixed="right">
            <template slot-scope="{ row }">
              <template v-if="row.status === 1 && canRegistration">
                <el-button type="text" @click="openAuditDialog(row, 'accept')">录用</el-button>
                <el-button type="text" class="danger-link" @click="openAuditDialog(row, 'reject')">拒绝</el-button>
              </template>
              <span v-else class="muted-text">{{ row.auditRemark || '-' }}</span>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      :title="eventForm.id ? '编辑赛事' : '新增赛事'"
      :visible.sync="eventDialogVisible"
      width="820px"
      top="5vh"
      custom-class="competition-event-dialog"
      @close="resetEventForm"
    >
      <div class="competition-event-dialog-body">
      <el-form ref="eventFormRef" :model="eventForm" :rules="eventRules" label-width="120px">
        <el-form-item label="赛事名称" prop="title">
          <el-input v-model="eventForm.title" maxlength="80" show-word-limit />
        </el-form-item>
        <el-form-item label="副标题">
          <el-input v-model="eventForm.subtitle" maxlength="120" show-word-limit />
        </el-form-item>
        <el-form-item label="主办方" prop="organizer">
          <el-input v-model="eventForm.organizer" maxlength="120" show-word-limit placeholder="请输入主办方名称" />
        </el-form-item>
        <el-form-item label="赛事图片">
          <div class="media-upload-actions">
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="uploadEventImage"
              accept="image/*"
            >
              <el-button icon="el-icon-picture-outline">上传图片</el-button>
            </el-upload>
          </div>
          <div v-if="eventImageList.length" class="event-image-list">
            <div
              v-for="(item, index) in eventImageList"
              :key="item.url + index"
              class="event-image-item"
              :class="{ 'is-cover': isCoverImage(item) }"
            >
              <img :src="fullUrl(item.url)" alt="">
              <div v-if="isCoverImage(item)" class="event-image-cover-tag">封面</div>
              <div class="event-image-actions">
                <el-button
                  v-if="!isCoverImage(item)"
                  type="text"
                  @click="setCoverImage(item)"
                >
                  设为封面
                </el-button>
                <span v-else class="cover-current-text">当前封面</span>
                <el-button type="text" class="danger-link" @click="removeEventImage(index)">删除</el-button>
              </div>
            </div>
          </div>
          <div v-else class="form-tip">上传赛事宣传图后，点击「设为封面」选择列表封面</div>
        </el-form-item>
        <el-form-item label="省市区" prop="regionCodes">
          <el-cascader
            v-model="eventForm.regionCodes"
            :options="regionOptions"
            :props="regionCascaderProps"
            filterable
            clearable
            placeholder="请选择省市区"
            style="width: 100%"
            @change="handleRegionChange"
          />
        </el-form-item>
        <el-form-item label="详细地址" prop="location">
          <el-input v-model="eventForm.location" maxlength="120" placeholder="街道、门牌号、场馆名称等" />
        </el-form-item>
        <el-form-item label="赛事时间" required>
          <div class="datetime-range">
            <el-date-picker
              v-model="eventForm.eventStartAt"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="开始时间"
              style="width: 100%"
            />
            <span class="range-separator">至</span>
            <el-date-picker
              v-model="eventForm.eventEndAt"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="结束时间"
              style="width: 100%"
            />
          </div>
        </el-form-item>
        <el-form-item label="报名时间" required>
          <div class="datetime-range">
            <el-date-picker
              v-model="eventForm.registrationStartAt"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="报名开始"
              style="width: 100%"
            />
            <span class="range-separator">至</span>
            <el-date-picker
              v-model="eventForm.registrationEndAt"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="报名截止"
              style="width: 100%"
            />
          </div>
        </el-form-item>
        <el-form-item label="报名费" prop="feeType">
          <div class="fee-row">
            <el-switch
              v-model="eventForm.feeEnabled"
              active-text="收取报名费"
              @change="handleFeeToggle"
            />
            <el-input-number
              v-if="eventForm.feeEnabled"
              v-model="eventForm.registrationFee"
              :min="0.01"
              :precision="2"
              :step="10"
              style="width: 180px; margin-left: 16px"
            />
            <span v-if="eventForm.feeEnabled" class="muted-text">元</span>
          </div>
        </el-form-item>
        <el-form-item label="录用名额">
          <el-input-number v-model="eventForm.quota" :min="1" :step="1" placeholder="不限" />
          <span class="form-tip">留空表示不限制录用人数</span>
        </el-form-item>
        <el-form-item label="赛事章程描述">
          <el-input v-model="eventForm.constitutionText" type="textarea" :rows="4" maxlength="5000" show-word-limit />
        </el-form-item>
        <el-form-item label="赛事奖励描述">
          <el-input v-model="eventForm.rewardText" type="textarea" :rows="4" maxlength="5000" show-word-limit />
        </el-form-item>
        <el-form-item label="赛事详情">
          <el-input v-model="eventForm.detailText" type="textarea" :rows="5" maxlength="5000" show-word-limit />
        </el-form-item>
      </el-form>
      </div>
      <span slot="footer">
        <el-button @click="eventDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="eventSaving" @click="submitEvent">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="auditAction === 'accept' ? '录用报名' : '拒绝报名'" :visible.sync="auditDialogVisible" width="520px">
      <el-form ref="auditFormRef" :model="auditForm" :rules="auditRules" label-width="90px">
        <el-form-item :label="auditAction === 'accept' ? '录用说明' : '拒绝原因'" prop="auditRemark">
          <el-input v-model="auditForm.auditRemark" type="textarea" :rows="4" maxlength="200" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="auditDialogVisible = false">取消</el-button>
        <el-button
          :type="auditAction === 'accept' ? 'primary' : 'danger'"
          :loading="auditSubmitting"
          @click="submitAudit"
        >
          确认{{ auditAction === 'accept' ? '录用' : '拒绝' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="艺人详情" :visible.sync="artistDetailVisible" width="920px" top="5vh">
      <div v-if="artistDetail.userId" v-loading="artistDetailLoading">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="艺名">{{ artistDetail.artistName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="昵称">{{ artistDetail.nickname || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ maskMobile(artistDetail.mobile) }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ artistDetail.userStatus === 1 ? '正常' : '封禁' }}</el-descriptions-item>
          <el-descriptions-item label="城市">{{ artistDetail.city || '-' }}</el-descriptions-item>
          <el-descriptions-item label="年龄">{{ artistDetail.age || '-' }}</el-descriptions-item>
          <el-descriptions-item label="身高">{{ artistDetail.heightCm || '-' }}</el-descriptions-item>
          <el-descriptions-item label="体重">{{ artistDetail.weightKg || '-' }}</el-descriptions-item>
          <el-descriptions-item label="技能标签" :span="2">{{ artistDetail.skillTags || '-' }}</el-descriptions-item>
          <el-descriptions-item label="通告偏好" :span="2">{{ artistDetail.noticeTypes || '-' }}</el-descriptions-item>
          <el-descriptions-item label="经验描述" :span="2">{{ artistDetail.experienceDesc || '-' }}</el-descriptions-item>
          <el-descriptions-item label="档期说明" :span="2">{{ artistDetail.scheduleDesc || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-divider>照片资料</el-divider>
        <div v-if="artistImageMediaList.length" class="media-grid">
          <el-image
            v-for="item in artistImageMediaList"
            :key="item.id || item.mediaUrl"
            :src="item.mediaUrl"
            :preview-src-list="artistPreviewImages"
            fit="cover"
            class="media-image"
          />
        </div>
        <el-empty v-else description="暂无照片资料" :image-size="88" />

        <el-divider>视频资料</el-divider>
        <div v-if="artistVideoMediaList.length" class="media-grid video-grid">
          <div v-for="item in artistVideoMediaList" :key="item.id || item.mediaUrl" class="video-card">
            <video
              class="media-video"
              :src="item.mediaUrl"
              :poster="item.coverUrl || ''"
              controls
              preload="metadata"
            />
            <div class="video-actions">
              <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无视频资料" :image-size="88" />

        <el-divider>艺人IP档案</el-divider>
        <div class="stat-grid">
          <div v-for="item in starOrderStatItems" :key="item.label" class="stat-item">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
          </div>
        </div>

        <el-table :data="starRecords" border class="star-table">
          <el-table-column label="类型" width="110">
            <template slot-scope="{ row }">{{ row.recordTypeText || recordTypeText(row.recordType) }}</template>
          </el-table-column>
          <el-table-column prop="recordTitle" label="标题" min-width="150" />
          <el-table-column prop="eventDate" label="日期" width="120" />
          <el-table-column prop="partnerName" label="机构/项目方" min-width="140" />
          <el-table-column prop="roleName" label="角色/身份" min-width="120" />
          <el-table-column prop="recordDesc" label="描述" min-width="200" show-overflow-tooltip />
          <el-table-column label="附件" min-width="220">
            <template slot-scope="{ row }">
              <div v-if="starAttachmentItems(row).length" class="star-attachment-grid">
                <div
                  v-for="(item, index) in starAttachmentItems(row)"
                  :key="`${item.mediaUrl}-${index}`"
                  class="star-attachment-item"
                >
                  <el-image
                    v-if="isImageAttachment(item)"
                    :src="item.mediaUrl"
                    :preview-src-list="starImagePreviewList(row)"
                    fit="cover"
                    class="star-attachment-thumb"
                  >
                    <div slot="error" class="star-attachment-error">图片加载失败</div>
                  </el-image>
                  <div v-else-if="isVideoAttachment(item)" class="star-attachment-video-card" @click="openVideoPreview(item)">
                    <video class="star-attachment-video" :src="item.mediaUrl" :poster="item.coverUrl || ''" muted preload="metadata" />
                    <div class="star-attachment-video-mask">
                      <span class="star-attachment-video-play">▶</span>
                      <span>视频</span>
                    </div>
                  </div>
                  <el-link v-else :href="item.mediaUrl" target="_blank" type="primary" class="star-attachment-file">查看附件</el-link>
                </div>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-if="!starRecords.length" description="暂无艺人IP档案" :image-size="88" />
      </div>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="960px"
      top="6vh"
      append-to-body
      @closed="handleVideoPreviewClosed"
    >
      <div class="video-preview-wrap">
        <video
          v-if="previewVideo.mediaUrl"
          ref="previewVideoPlayer"
          class="preview-video-player"
          :src="previewVideo.mediaUrl"
          :poster="previewVideo.coverUrl || ''"
          controls
          autoplay
          preload="auto"
        />
      </div>
      <span slot="footer">
        <el-button @click="videoPreviewVisible = false">关闭</el-button>
        <el-button type="primary" @click="requestVideoFullscreen">全屏播放</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  acceptCompetitionRegistration,
  createCompetitionEvent,
  getCompetitionEvents,
  getCompetitionRegistrations,
  getCompetitionRegions,
  offlineCompetitionEvent,
  publishCompetitionEvent,
  rejectCompetitionRegistration,
  updateCompetitionEvent
} from '../../../api/competition'
import request, { API_BASE_URL } from '../../../utils/request'
import { getArtistUserDetail } from '../../../api/platformManage'
import { hasPermission } from '../../../utils/auth'
import { isImageMedia, isVideoMedia, maskMobile, normalizeMediaItem } from '../../../utils/format'

const eventStatusOptions = [
  { label: '草稿', value: 1 },
  { label: '已发布', value: 2 },
  { label: '已下架', value: 3 }
]

const registrationStatusOptions = [
  { label: '待审核', value: 1 },
  { label: '待付款', value: 4 },
  { label: '已录用', value: 2 },
  { label: '已拒绝', value: 3 }
]

function defaultEventForm() {
  return {
    id: '',
    title: '',
    subtitle: '',
    organizer: '',
    coverUrl: '',
    regionCodes: [],
    provinceCode: '',
    provinceName: '',
    cityCode: '',
    cityName: '',
    districtCode: '',
    districtName: '',
    location: '',
    eventStartAt: '',
    eventEndAt: '',
    registrationStartAt: '',
    registrationEndAt: '',
    feeEnabled: false,
    feeType: 1,
    registrationFee: 0,
    quota: null,
    constitutionText: '',
    rewardText: '',
    detailText: '',
    imageUrls: ''
  }
}

const municipalityCityAliases = ['市辖区']

function isMunicipalityProvince(province) {
  const cities = (province && province.cities) || []
  return cities.length === 1 && municipalityCityAliases.includes(cities[0].name)
}

function normalizeCityDisplayName(provinceName, cityName) {
  if (!cityName || municipalityCityAliases.includes(cityName)) {
    return provinceName || cityName || ''
  }
  return cityName
}

function mapRegionTree(list) {
  return (list || []).map(province => {
    if (isMunicipalityProvince(province)) {
      const city = province.cities[0]
      return {
        code: province.code,
        name: province.name,
        municipality: true,
        cityCode: city.code,
        children: (city.districts || []).map(district => ({
          code: district.code,
          name: district.name
        }))
      }
    }
    return {
      code: province.code,
      name: province.name,
      children: (province.cities || []).map(city => ({
        code: city.code,
        name: normalizeCityDisplayName(province.name, city.name),
        children: (city.districts || []).map(district => ({
          code: district.code,
          name: district.name
        }))
      }))
    }
  })
}

function buildRegionCodes(row, regionOptions) {
  if (!row || !row.provinceCode) return []
  const province = (regionOptions || []).find(item => item.code === row.provinceCode)
  if (province && province.municipality) {
    return [row.provinceCode, row.districtCode].filter(Boolean)
  }
  if (row.cityName === '市辖区' || row.cityName === row.provinceName) {
    const matched = (regionOptions || []).find(item => item.code === row.provinceCode)
    if (matched && matched.municipality) {
      return [row.provinceCode, row.districtCode].filter(Boolean)
    }
  }
  return [row.provinceCode, row.cityCode, row.districtCode].filter(Boolean)
}

export default {
  name: 'PlatformCompetitionPage',
  data() {
    return {
      activeTab: 'events',
      eventLoading: false,
      registrationLoading: false,
      eventSaving: false,
      auditSubmitting: false,
      events: [],
      registrations: [],
      eventOptions: [],
      regionOptions: [],
      eventImageList: [],
      regionCascaderProps: {
        value: 'code',
        label: 'name',
        children: 'children'
      },
      eventQuery: {
        keyword: '',
        status: null
      },
      registrationQuery: {
        competitionId: '',
        status: 1,
        keyword: ''
      },
      eventStatusOptions,
      registrationStatusOptions,
      eventDialogVisible: false,
      eventForm: defaultEventForm(),
      eventRules: {
        title: [{ required: true, message: '请输入赛事名称', trigger: 'blur' }],
        organizer: [{ required: true, message: '请输入主办方', trigger: 'blur' }],
        regionCodes: [{
          validator(rule, value, callback) {
            if (!value || value.length < 2) {
              callback(new Error('请选择省市区'))
              return
            }
            callback()
          },
          trigger: 'change'
        }],
        feeType: [{ required: true, message: '请设置报名费', trigger: 'change' }]
      },
      auditDialogVisible: false,
      auditAction: 'accept',
      auditTarget: null,
      auditForm: {
        auditRemark: ''
      },
      auditRules: {
        auditRemark: [{ required: true, message: '请填写说明', trigger: 'blur' }]
      },
      artistDetailVisible: false,
      artistDetailLoading: false,
      artistDetail: {},
      videoPreviewVisible: false,
      previewVideo: {}
    }
  },
  computed: {
    artistMediaList() {
      return (this.artistDetail.mediaList || [])
        .map(item => normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item))
        .filter(Boolean)
    },
    artistImageMediaList() {
      return this.artistMediaList.filter(item => isImageMedia(item))
    },
    artistVideoMediaList() {
      return this.artistMediaList.filter(item => isVideoMedia(item))
    },
    artistPreviewImages() {
      return this.artistImageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    starProfile() {
      return this.artistDetail.starProfile || {}
    },
    starRecords() {
      return this.starProfile.records || []
    },
    starOrderStats() {
      return this.starProfile.orderStats || {}
    },
    starOrderStatItems() {
      return [
        { label: '累计接单', value: this.starOrderStats.totalOrderCount || 0 },
        { label: '进行中', value: this.starOrderStats.inProgressOrderCount || 0 },
        { label: '已完成', value: this.starOrderStats.completedOrderCount || 0 },
        { label: '已结算', value: this.starOrderStats.settledOrderCount || 0 },
        { label: '异常关闭', value: this.starOrderStats.closedOrderCount || 0 }
      ]
    },
    canCreate() {
      return hasPermission('competition_center:create')
    },
    canUpdate() {
      return hasPermission('competition_center:update')
    },
    canPublish() {
      return hasPermission('competition_center:publish')
    },
    canOffline() {
      return hasPermission('competition_center:offline')
    },
    canRegistration() {
      return hasPermission('competition_center:registration')
    }
  },
  created() {
    this.fetchRegions()
    this.refreshAll()
  },
  methods: {
    maskMobile,
    canViewArtistDetail(row) {
      return row && (row.status === 1 || row.status === 2 || row.status === 4)
    },
    async openArtistDetail(row) {
      if (!row || !row.artistUserId) return
      this.artistDetailVisible = true
      this.artistDetailLoading = true
      this.artistDetail = {}
      try {
        this.artistDetail = await getArtistUserDetail(String(row.artistUserId))
      } catch (error) {
        this.artistDetailVisible = false
        this.$message.error(error.message || '加载艺人资料失败')
      } finally {
        this.artistDetailLoading = false
      }
    },
    openVideoPreview(item) {
      this.previewVideo = {
        mediaUrl: item.mediaUrl || '',
        coverUrl: item.coverUrl || ''
      }
      this.videoPreviewVisible = true
    },
    handleVideoPreviewClosed() {
      const player = this.$refs.previewVideoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
        player.currentTime = 0
      }
      this.previewVideo = {}
    },
    async requestVideoFullscreen() {
      const player = this.$refs.previewVideoPlayer
      if (!player) return
      const requestFullscreen =
        player.requestFullscreen ||
        player.webkitRequestFullscreen ||
        player.mozRequestFullScreen ||
        player.msRequestFullscreen
      if (!requestFullscreen) {
        this.$message.warning('当前浏览器不支持全屏播放')
        return
      }
      await requestFullscreen.call(player)
    },
    normalizeStarAttachment(item) {
      if (!item) return null
      return normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item)
    },
    starAttachmentItems(row) {
      return ((row && row.attachments) || [])
        .map(item => this.normalizeStarAttachment(item))
        .filter(Boolean)
    },
    starImagePreviewList(row) {
      return this.starAttachmentItems(row)
        .filter(item => this.isImageAttachment(item))
        .map(item => item.mediaUrl)
    },
    isImageAttachment(item) {
      return isImageMedia(item)
    },
    isVideoAttachment(item) {
      return isVideoMedia(item)
    },
    recordTypeText(type) {
      if (Number(type) === 1) return '成长记录'
      if (Number(type) === 2) return '获奖'
      if (Number(type) === 3) return '合作经历'
      return '其他'
    },
    async fetchRegions() {
      try {
        const data = await getCompetitionRegions()
        this.regionOptions = mapRegionTree(Array.isArray(data) ? data : [])
      } catch (error) {
        this.regionOptions = []
        this.$message.error('省市区数据加载失败')
      }
    },
    async refreshAll() {
      await Promise.all([this.fetchEvents(), this.fetchEventOptions()])
      if (this.activeTab === 'registrations') {
        await this.fetchRegistrations()
      }
    },
    handleTabChange() {
      if (this.activeTab === 'registrations') {
        this.fetchRegistrations()
      }
    },
    async fetchEvents() {
      this.eventLoading = true
      try {
        const data = await getCompetitionEvents({
          keyword: this.eventQuery.keyword || undefined,
          status: this.eventQuery.status || undefined
        })
        this.events = Array.isArray(data) ? data : []
      } finally {
        this.eventLoading = false
      }
    },
    async fetchEventOptions() {
      const data = await getCompetitionEvents({})
      this.eventOptions = Array.isArray(data) ? data : []
    },
    async fetchRegistrations() {
      this.registrationLoading = true
      try {
        const data = await getCompetitionRegistrations({
          competitionId: this.registrationQuery.competitionId || undefined,
          status: this.registrationQuery.status || undefined,
          keyword: this.registrationQuery.keyword || undefined
        })
        this.registrations = Array.isArray(data) ? data : []
      } finally {
        this.registrationLoading = false
      }
    },
    openEventDialog(row) {
      if (row) {
        const regionCodes = buildRegionCodes(row, this.regionOptions)
        this.eventForm = {
          ...defaultEventForm(),
          ...row,
          id: row.id ? String(row.id) : '',
          regionCodes,
          feeEnabled: row.feeType === 2,
          feeType: row.feeType || 1,
          registrationFee: row.registrationFee || 0,
          quota: row.quota || null
        }
        this.eventImageList = this.parseEventImages(row.imageUrls, row.coverUrl)
      } else {
        this.eventForm = defaultEventForm()
        this.eventImageList = []
      }
      this.syncCoverFromImages()
      this.eventDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.eventFormRef) {
          this.$refs.eventFormRef.clearValidate()
        }
      })
    },
    resetEventForm() {
      this.eventForm = defaultEventForm()
      this.eventImageList = []
    },
    handleRegionChange(codes) {
      const names = this.resolveRegionSelection(codes)
      this.eventForm.provinceCode = names.provinceCode
      this.eventForm.provinceName = names.provinceName
      this.eventForm.cityCode = names.cityCode
      this.eventForm.cityName = names.cityName
      this.eventForm.districtCode = names.districtCode
      this.eventForm.districtName = names.districtName
    },
    resolveRegionSelection(codes) {
      const result = {
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        districtCode: '',
        districtName: ''
      }
      if (!codes || !codes.length) return result
      const province = (this.regionOptions || []).find(item => item.code === codes[0])
      if (!province) return result
      result.provinceCode = province.code
      result.provinceName = province.name

      if (province.municipality && codes.length === 2) {
        result.cityCode = province.cityCode
        result.cityName = province.name
        const district = (province.children || []).find(item => item.code === codes[1])
        if (district) {
          result.districtCode = district.code
          result.districtName = district.name
        }
        return result
      }

      const city = (province.children || []).find(item => item.code === codes[1])
      if (!city) return result
      result.cityCode = city.code
      result.cityName = city.name
      const district = (city.children || []).find(item => item.code === codes[2])
      if (district) {
        result.districtCode = district.code
        result.districtName = district.name
      }
      return result
    },
    resolveRegionNames(codes) {
      const selection = this.resolveRegionSelection(codes)
      return {
        provinceName: selection.provinceName,
        cityName: selection.cityName,
        districtName: selection.districtName
      }
    },
    handleFeeToggle(enabled) {
      this.eventForm.feeType = enabled ? 2 : 1
      if (!enabled) {
        this.eventForm.registrationFee = 0
      } else if (!this.eventForm.registrationFee || this.eventForm.registrationFee <= 0) {
        this.eventForm.registrationFee = 99
      }
    },
    submitEvent() {
      this.$refs.eventFormRef.validate(async valid => {
        if (!valid) return
        this.handleRegionChange(this.eventForm.regionCodes)
        if (!this.eventForm.provinceCode || !this.eventForm.cityCode || !this.eventForm.districtCode) {
          this.$message.warning('请选择省市区')
          return
        }
        this.syncCoverFromImages()
        this.eventSaving = true
        try {
          const payload = {
            title: this.eventForm.title,
            subtitle: this.eventForm.subtitle,
            organizer: this.eventForm.organizer,
            coverUrl: this.eventForm.coverUrl || '',
            provinceCode: this.eventForm.provinceCode,
            provinceName: this.eventForm.provinceName,
            cityCode: this.eventForm.cityCode,
            cityName: this.eventForm.cityName,
            districtCode: this.eventForm.districtCode,
            districtName: this.eventForm.districtName,
            location: this.eventForm.location,
            imageUrls: JSON.stringify(this.eventImageList),
            eventStartAt: this.eventForm.eventStartAt,
            eventEndAt: this.eventForm.eventEndAt,
            registrationStartAt: this.eventForm.registrationStartAt,
            registrationEndAt: this.eventForm.registrationEndAt,
            feeType: this.eventForm.feeEnabled ? 2 : 1,
            registrationFee: this.eventForm.feeEnabled ? this.eventForm.registrationFee : 0,
            quota: this.eventForm.quota || null,
            constitutionText: this.eventForm.constitutionText,
            rewardText: this.eventForm.rewardText,
            detailText: this.eventForm.detailText
          }
          if (this.eventForm.id) {
            await updateCompetitionEvent(this.eventForm.id, payload)
          } else {
            await createCompetitionEvent(payload)
          }
          this.$message.success('赛事保存成功')
          this.eventDialogVisible = false
          await this.refreshAll()
        } finally {
          this.eventSaving = false
        }
      })
    },
    async uploadEventImage(option) {
      await this.uploadImage(option, url => {
        this.eventImageList.push({ type: 'image', url })
        this.syncCoverFromImages()
      }, '图片上传成功')
    },
    async uploadImage(option, onSuccess, successMessage) {
      const file = option && option.file
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      try {
        const data = await request({
          url: '/api/common/upload',
          method: 'post',
          data: formData,
          timeout: 120000
        })
        if (!data || !data.url) {
          throw new Error('上传失败')
        }
        onSuccess(data.url)
        this.$message.success(successMessage)
        if (option.onSuccess) option.onSuccess(data)
      } catch (error) {
        this.$message.error(error.message || '上传失败')
        if (option.onError) option.onError(error)
      }
    },
    setCoverImage(item) {
      if (!item || !item.url) return
      this.eventForm.coverUrl = item.url
    },
    isCoverImage(item) {
      return !!(item && item.url && item.url === this.eventForm.coverUrl)
    },
    removeEventImage(index) {
      const removed = this.eventImageList.splice(index, 1)[0]
      if (removed && removed.url === this.eventForm.coverUrl) {
        this.eventForm.coverUrl = ''
      }
      this.syncCoverFromImages()
    },
    syncCoverFromImages() {
      if (this.eventForm.coverUrl && this.eventImageList.some(item => item.url === this.eventForm.coverUrl)) {
        return
      }
      const firstImage = this.eventImageList.find(item => item.url)
      this.eventForm.coverUrl = firstImage ? firstImage.url : ''
    },
    parseEventImages(rawValue, coverUrl) {
      const list = []
      if (rawValue) {
        try {
          const parsed = JSON.parse(rawValue)
          if (Array.isArray(parsed)) {
            parsed.forEach(item => {
              if (typeof item === 'string' && item) {
                list.push({ type: 'image', url: item })
              } else if (item && item.url) {
                list.push({ type: 'image', url: item.url })
              }
            })
          }
        } catch (error) {
          rawValue.split(',').map(item => item.trim()).filter(Boolean).forEach(url => {
            list.push({ type: 'image', url })
          })
        }
      }
      if (coverUrl && !list.some(item => item.url === coverUrl)) {
        list.unshift({ type: 'image', url: coverUrl })
      }
      return list
    },
    locationLabel(row) {
      if (!row) return '-'
      const cityName = normalizeCityDisplayName(row.provinceName, row.cityName)
      const parts = [row.provinceName, cityName, row.districtName, row.location].filter(Boolean)
      const deduped = parts.filter((part, index) => index === 0 || part !== parts[index - 1])
      return deduped.length ? deduped.join(' ') : '-'
    },
    async publishEvent(row) {
      await MessageBox.confirm(`确认发布赛事「${row.title}」？`, '发布赛事', { type: 'warning' })
      await publishCompetitionEvent(row.id)
      this.$message.success('赛事已发布')
      await this.fetchEvents()
    },
    async offlineEvent(row) {
      await MessageBox.confirm(`确认下架赛事「${row.title}」？`, '下架赛事', { type: 'warning' })
      await offlineCompetitionEvent(row.id)
      this.$message.success('赛事已下架')
      await this.fetchEvents()
    },
    viewRegistrations(row) {
      this.activeTab = 'registrations'
      this.registrationQuery.competitionId = row.id
      this.registrationQuery.status = 1
      this.fetchRegistrations()
    },
    openAuditDialog(row, action) {
      this.auditAction = action
      this.auditTarget = row
      this.auditForm.auditRemark = action === 'accept' ? '恭喜录用，请留意后续通知。' : ''
      this.auditDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.auditFormRef) {
          this.$refs.auditFormRef.clearValidate()
        }
      })
    },
    submitAudit() {
      this.$refs.auditFormRef.validate(async valid => {
        if (!valid || !this.auditTarget) return
        this.auditSubmitting = true
        try {
          const payload = { auditRemark: this.auditForm.auditRemark }
          if (this.auditAction === 'accept') {
            await acceptCompetitionRegistration(this.auditTarget.id, payload)
            const requiresPay = Number(this.auditTarget.payAmount) > 0 && Number(this.auditTarget.payStatus) !== 2
            this.$message.success(requiresPay ? '已录用，等待艺人付款' : '已录用')
          } else {
            await rejectCompetitionRegistration(this.auditTarget.id, payload)
            this.$message.success('已拒绝')
          }
          this.auditDialogVisible = false
          await Promise.all([this.fetchRegistrations(), this.fetchEvents()])
        } finally {
          this.auditSubmitting = false
        }
      })
    },
    feeLabel(row) {
      if (!row || row.feeType !== 2) return '免费'
      return `¥${row.registrationFee || 0}`
    },
    eventStatusLabel(status) {
      const item = eventStatusOptions.find(option => option.value === status)
      return item ? item.label : '-'
    },
    eventStatusTagType(status) {
      if (status === 2) return 'success'
      if (status === 3) return 'info'
      return 'warning'
    },
    registrationStatusLabel(status) {
      const item = registrationStatusOptions.find(option => option.value === status)
      return item ? item.label : '-'
    },
    registrationStatusTagType(status) {
      if (status === 2) return 'success'
      if (status === 3) return 'danger'
      if (status === 4) return 'warning'
      return 'warning'
    },
    payStatusLabel(status) {
      if (status === 2) return '已支付'
      if (status === 3) return '免费'
      return '待支付'
    },
    fullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) return url
      if (url.startsWith('/uploads/')) {
        const uploadPath = url.replace('/uploads/', '')
        return `${API_BASE_URL}/api/common/file/${uploadPath}`
      }
      return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`
    }
  }
}
</script>

<style scoped>
.competition-page .toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.artist-info {
  min-width: 0;
}
.artist-detail-link {
  padding: 0;
  margin-top: 2px;
}
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}
.media-image {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  background: #f5f7fa;
}
.video-grid {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}
.video-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.media-video {
  width: 100%;
  height: 180px;
  display: block;
  border-radius: 8px;
  background: #111827;
  object-fit: contain;
}
.video-actions {
  display: flex;
  justify-content: flex-end;
}
.video-preview-wrap {
  display: flex;
  justify-content: center;
}
.preview-video-player {
  width: 100%;
  max-height: 72vh;
  border-radius: 10px;
  background: #111827;
}
.stat-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.stat-item {
  padding: 14px 10px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
  text-align: center;
}
.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}
.stat-label {
  margin-top: 6px;
  color: #909399;
}
.star-table {
  margin-top: 12px;
}
.star-attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.star-attachment-item {
  width: 64px;
  height: 64px;
}
.star-attachment-thumb,
.star-attachment-video-card {
  width: 64px;
  height: 64px;
  border-radius: 6px;
}
.star-attachment-thumb {
  background: #f5f7fa;
}
.star-attachment-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 12px;
  text-align: center;
  background: #f5f7fa;
}
.star-attachment-video-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #111827;
}
.star-attachment-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.star-attachment-video-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  font-size: 12px;
  background: rgba(17, 24, 39, 0.42);
}
.star-attachment-video-play {
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 50%;
  text-align: center;
  background: rgba(255, 255, 255, 0.22);
}
.star-attachment-file {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #f5f7fa;
}
.event-cell,
.artist-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.event-cover,
.artist-avatar {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}
.event-cover-empty,
.artist-avatar-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 12px;
}
.event-title {
  font-weight: 600;
}
.event-subtitle,
.muted-text,
.form-tip {
  color: #909399;
  font-size: 12px;
}
.datetime-range {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;
}
.range-separator {
  color: #909399;
}
.fee-row {
  display: flex;
  align-items: center;
}
.media-upload-actions {
  margin-bottom: 12px;
}
.event-image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.event-image-item {
  width: 120px;
  position: relative;
}
.event-image-item.is-cover img {
  border: 2px solid #409eff;
}
.event-image-cover-tag {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(64, 158, 255, 0.92);
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
}
.cover-current-text {
  color: #409eff;
  font-size: 12px;
}
.event-image-item img {
  width: 120px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}
.event-image-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.danger-link {
  color: #f56c6c;
}
</style>

<style>
.competition-event-dialog .el-dialog__body {
  max-height: 72vh;
  overflow-y: auto;
  padding-top: 10px;
}
.competition-event-dialog-body {
  padding-right: 4px;
}
</style>
